import { ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@microservices/wiskey-react-components'
import { Button, Grid, SxProps, Theme } from '@mui/material'
import { CellContext, ColumnDef } from '@tanstack/react-table'

import { AddDisplayRulesValueDialog } from '@mobilePages/DisplayRulesCreateOrEdit/components/AddDisplayRulesValueDialog'

import { LoadingButton } from '@components/LoadingButton'
import { SearchInTable } from '@components/SwitchTable/SearchInTable'

import { ENTITY, MODAL_TYPE, MODEL_TYPE } from '@constants'

import { ModalType, ShareActionType } from '../../types'
import { ReactTable } from '../ui/ReactTable'

type SwitchTableProps<T> = {
  columns: ColumnDef<T>[]
  rows: T[]
  btnText?: string
  loading?: boolean
  type?: ShareActionType
  page?: number
  isCrud?: boolean
  pageSize?: number
  btnDisabled?: boolean
  disablePagination?: boolean
  useDragAndDrop?: boolean
  dragHandleHeader?: string
  disableSort?: boolean
  showActionsColumn?: boolean
  showVisibilityColumn?: boolean
  onChangePage?: (event: ChangeEvent<unknown>, value: number) => void
  onEdit?: (props: CellContext<T, unknown>) => void
  onHide?: (props: CellContext<T, unknown>, checked: boolean) => void
  onDelete?: (id: string | number) => void
  onDropChange?: (rows: T[], row: T, newIndex: number) => T[]
  onShowDialog?: (type: ModalType, id?: string | number, isAction?: boolean) => void
  showDialog?: boolean
  showSearch?: boolean
  disabledVisibilityColumn?: boolean
  disabledDeleteActionColumn?: boolean
  disabledEditActionColumn?: boolean
  disabledCopyActionColumn?: boolean
  containerSx?: SxProps<Theme>
  updateRow?: (data: T) => void
  onCreateAllEntityFields?: (onlyRequired?: boolean) => void
  onDeleteAllEntityFields?: () => void
  createAllBtnText?: string
  deleteAllBtnText?: string
  createAllRequiredBtnText?: string
  isCreateAllLoading?: boolean
  isDeleteAllLoading?: boolean
  isCreateMandatoryLoading?: boolean
  additionalElementHeaderTable?: React.ReactNode
  rowHeight?: number
}

const getDialogType = (type: ShareActionType): MODEL_TYPE => {
  switch (type) {
    case MODEL_TYPE.DISPLAY_RULES:
      return MODEL_TYPE.DISPLAY_RULES
    default:
      return MODEL_TYPE.DISPLAY_RULES
  }
}

export const SwitchTable = <T extends { id: string | number }>({
  columns,
  rows,
  btnText,
  type,
  btnDisabled,
  loading,
  showDialog = false,
  useDragAndDrop,
  dragHandleHeader,
  disablePagination,
  disableSort,
  showActionsColumn,
  showVisibilityColumn,
  onEdit,
  onHide,
  onDropChange,
  onDelete,
  onShowDialog,
  showSearch,
  disabledVisibilityColumn,
  disabledEditActionColumn,
  disabledDeleteActionColumn,
  disabledCopyActionColumn,
  containerSx,
  updateRow,
  onCreateAllEntityFields,
  onDeleteAllEntityFields,
  createAllBtnText,
  deleteAllBtnText,
  createAllRequiredBtnText,
  isCreateAllLoading = false,
  isDeleteAllLoading = false,
  isCreateMandatoryLoading = false,
  additionalElementHeaderTable,
  rowHeight,
}: SwitchTableProps<T>) => {
  const { t } = useTranslation()
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCreateAllColumnsModal, setShowCreateAllColumnsModal] = useState(false)
  const [showDeleteAllColumnsModal, setShowDeleteAllColumnsModal] = useState(false)
  const [showCreateMandatoryColumnsModal, setShowCreateMandatoryColumnsModal] = useState(false)
  const [currentRowId, setCurrentRowId] = useState<number | string | null>()
  const [globalFilter, setGlobalFilter] = useState('')
  const dialogType: MODEL_TYPE | null = type ? getDialogType(type) : null

  const handleClickDelete = (params?: CellContext<T, unknown>) => {
    if (params?.row.original.id && !showDeleteModal) {
      setCurrentRowId(params?.row.original.id)
      setShowDeleteModal(true)

      return
    }

    if (currentRowId) {
      onDelete?.(currentRowId)
      setCurrentRowId(null)
      setShowDeleteModal(false)

      return
    }

    if (params?.row.original.id) {
      onDelete?.(params.row.original.id)
    }
  }

  const isShowSearch = (type: ShareActionType | ENTITY | undefined): boolean => {
    return (
      (Object.values(ENTITY).includes(type as ENTITY) && showSearch) ||
      type === MODEL_TYPE.DISPLAY_RULES
    )
  }

  const handleCloseDeleteModal = () => setShowDeleteModal(false)

  const handleSetSearch = (pattern: string) => {
    setGlobalFilter(pattern)
  }

  const handleShowCreateAllColumnsModal = () => {
    setShowCreateAllColumnsModal(true)
  }

  const handleCloseCreateAllColumnsModal = () => {
    setShowCreateAllColumnsModal(false)
  }

  const handleClickCreateAll = () => {
    handleCloseCreateAllColumnsModal()
    onCreateAllEntityFields?.()
  }

  const handleShowDeleteAllColumnsModal = () => {
    setShowDeleteAllColumnsModal(true)
  }

  const handleCloseDeleteAllColumnsModal = () => {
    setShowDeleteAllColumnsModal(false)
  }

  const handleClickDeleteAll = () => {
    handleCloseDeleteAllColumnsModal()
    onDeleteAllEntityFields?.()
  }

  const handleShowCreateMandatoryColumnsModal = () => {
    setShowCreateMandatoryColumnsModal(true)
  }

  const handleCloseCreateMandatoryColumnsModal = () => {
    setShowCreateMandatoryColumnsModal(false)
  }

  const handleClickCreateMandatory = () => {
    handleCloseCreateMandatoryColumnsModal()
    onCreateAllEntityFields?.(true)
  }

  const renderDialog = useCallback(() => {
    if (dialogType === MODEL_TYPE.DISPLAY_RULES) {
      return <AddDisplayRulesValueDialog />
    }

    return null
  }, [dialogType])

  return (
    <Grid container flexDirection='column' spacing={1}>
      <Grid
        container
        item
        alignItems='center'
        justifyContent='space-between'
        sx={{ pl: '0px !important' }}
      >
        <Grid item>
          {isShowSearch(type) && (
            <SearchInTable searchValue={globalFilter} setGlobalFilter={handleSetSearch} />
          )}
          {additionalElementHeaderTable}
        </Grid>
        <Grid item>
          {deleteAllBtnText && (
            <LoadingButton
              disabled={btnDisabled}
              loading={isDeleteAllLoading}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={handleShowDeleteAllColumnsModal}
            >
              {deleteAllBtnText}
            </LoadingButton>
          )}
          {createAllBtnText && (
            <LoadingButton
              disabled={btnDisabled}
              loading={isCreateAllLoading}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={handleShowCreateAllColumnsModal}
            >
              {createAllBtnText}
            </LoadingButton>
          )}
          {createAllRequiredBtnText && (
            <LoadingButton
              disabled={btnDisabled}
              loading={isCreateMandatoryLoading}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={handleShowCreateMandatoryColumnsModal}
            >
              {createAllRequiredBtnText}
            </LoadingButton>
          )}
          {btnText && (
            <Button
              disabled={btnDisabled}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={() => onShowDialog?.(MODAL_TYPE.CREATE)}
            >
              {btnText}
            </Button>
          )}
        </Grid>
      </Grid>
      {showDialog && renderDialog()}
      <ConfirmModal
        actionBtnText={t('modal.delete.btn')}
        isShow={showDeleteModal}
        text={t('modal.delete.warning')}
        title={t('modal.delete.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        onClose={handleCloseDeleteModal}
        onConfirm={handleClickDelete}
      />

      <ConfirmModal
        actionBtnText={t('modal.delete.btn')}
        isShow={showDeleteAllColumnsModal}
        text={t('modal.delete.warning')}
        title={t('modal.delete.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        onClose={handleCloseDeleteAllColumnsModal}
        onConfirm={handleClickDeleteAll}
      />

      <ConfirmModal
        actionBtnText={t('modal.createAll.actionButton')}
        isShow={showCreateAllColumnsModal}
        title={t('modal.createAll.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        text={t('modal.createAll.text', {
          entityField: dialogType,
        })}
        onClose={handleCloseCreateAllColumnsModal}
        onConfirm={handleClickCreateAll}
      />

      <ConfirmModal
        actionBtnText={t('modal.createMandatory.actionButton')}
        isShow={showCreateMandatoryColumnsModal}
        title={t('modal.createMandatory.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        text={t('modal.createMandatory.text', {
          entityField: dialogType,
        })}
        onClose={handleCloseCreateMandatoryColumnsModal}
        onConfirm={handleClickCreateMandatory}
      />

      <Grid item sx={[{ pl: '0px !important' }, Boolean(useDragAndDrop) && { userSelect: 'none' }]}>
        <ReactTable
          columns={columns}
          containerSx={containerSx}
          data={rows}
          disablePagination={disablePagination}
          disableSort={disableSort}
          disabledCopyActionColumn={disabledCopyActionColumn}
          disabledDeleteActionColumn={disabledDeleteActionColumn}
          disabledEditActionColumn={disabledEditActionColumn}
          disabledVisibilityColumn={disabledVisibilityColumn}
          dragHandleHeader={dragHandleHeader}
          globalFilter={globalFilter}
          loading={loading}
          rowHeight={rowHeight}
          setGlobalFilter={setGlobalFilter}
          showActionsColumn={showActionsColumn}
          showVisibilityColumn={showVisibilityColumn}
          updateRow={updateRow}
          useDragAndDrop={useDragAndDrop}
          onDelete={handleClickDelete}
          onDropChange={onDropChange}
          onEdit={onEdit}
          onHide={onHide}
        />
      </Grid>
    </Grid>
  )
}
